/**
 * CodeConversationsView — the Code tab.
 *
 * The Code tab is a list of CONVERSATIONS across every coding group
 * (`pwa-auto-code-claude` + `pwa-auto-code-codex`), not a list of the two group
 * rows. Mark's coding surface is one inbox: compose picks the agent, each row
 * says which agent owns it, and one tap archives.
 *
 * Sections:
 *   - Active   (not closed) — sorted by last activity DESC
 *   - Archived (closed)     — sorted by closed_at DESC, collapsed by default
 *
 * Rows are `GroupTopicRow` (the same row the automation groups use) with the
 * agent badge in its `leadingBadge` slot and the always-visible left action
 * rail turned on, so archive/reopen is one tap with no long-press.
 *
 * The per-group `GroupListView` is NOT reachable for these chats: ChatPage
 * redirects a coding-group root to this tab (see `codeGroupRootRedirect`).
 */
import { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, Archive, MessageSquarePlus } from 'lucide-react'
import {
  useTopics, useCloseTopic, useReopenTopic, type Topic,
} from '../lib/api'
import {
  CODE_AGENTS, chatTarget, codeArchiveDestination, type CodeAgent,
} from '../lib/chatNavigation'
import { draftTargetKey, isVisibleDraft, useUi } from '../store/ui'
import AgentBadge from './AgentBadge'
import GroupTopicRow from './GroupTopicRow'
import UndoToast from './UndoToast'

type CodeRow = {
  chatId: string
  agent: CodeAgent
  label: string
  topic: Topic
}

const [CLAUDE_CODE, CODEX_CODE] = CODE_AGENTS

function lastActivityTs(topic: Topic): number {
  return topic.last_message_ts ?? topic.created_at
}

export default function CodeConversationsView() {
  const navigate = useNavigate()
  const location = useLocation()
  // One `useTopics` per coding group. CODE_AGENTS is a frozen literal, so the
  // hook count is constant across renders — destructured explicitly rather than
  // mapped so that stays true by construction.
  const claudeTopics = useTopics(CLAUDE_CODE.chatId)
  const codexTopics = useTopics(CODEX_CODE.chatId)
  const closeClaude = useCloseTopic(CLAUDE_CODE.chatId)
  const closeCodex = useCloseTopic(CODEX_CODE.chatId)
  const reopenClaude = useReopenTopic(CLAUDE_CODE.chatId)
  const reopenCodex = useReopenTopic(CODEX_CODE.chatId)

  const draftsByChat = useUi((s) => s.draftsByChat)

  const [archiveOpen, setArchiveOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [undo, setUndo] = useState<{ chatId: string; topicId: string; message: string } | null>(null)

  const closeFor = useCallback(
    (chatId: string) => (chatId === CODEX_CODE.chatId ? closeCodex : closeClaude),
    [closeClaude, closeCodex],
  )
  const reopenFor = useCallback(
    (chatId: string) => (chatId === CODEX_CODE.chatId ? reopenCodex : reopenClaude),
    [reopenClaude, reopenCodex],
  )

  const rows = useMemo<CodeRow[]>(() => {
    const collected: CodeRow[] = []
    for (const [source, meta] of [
      [claudeTopics.data, CLAUDE_CODE] as const,
      [codexTopics.data, CODEX_CODE] as const,
    ]) {
      for (const topic of source ?? []) {
        collected.push({ chatId: meta.chatId, agent: meta.agent, label: meta.label, topic })
      }
    }
    return collected
  }, [claudeTopics.data, codexTopics.data])

  const visible = rows

  const active = useMemo(() => (
    visible
      .filter((r) => !r.topic.closed)
      .sort((a, b) => lastActivityTs(b.topic) - lastActivityTs(a.topic))
  ), [visible])

  const archived = useMemo(() => (
    visible
      .filter((r) => r.topic.closed)
      .sort((a, b) => (b.topic.closed_at ?? 0) - (a.topic.closed_at ?? 0))
  ), [visible])

  const openRow = useCallback((row: CodeRow, options?: { focusComposer?: boolean }) => {
    const href = chatTarget(row.chatId, row.topic.id, { parentTab: 'code' })
    navigate(options?.focusComposer ? `${href}&focus_composer=1` : href)
  }, [navigate])

  const archiveRow = useCallback(async (row: CodeRow): Promise<boolean> => {
    setActionError(null)
    const destination = codeArchiveDestination(
      new URL(`${location.pathname}${location.search}`, 'https://agentchat.local'),
      { chatId: row.chatId, topicId: row.topic.id },
      active.map((candidate) => ({
        chatId: candidate.chatId,
        topicId: candidate.topic.id,
      })),
    )
    try {
      // Coding conversations have no Todoist task behind them, so the close is
      // topic-only.
      await closeFor(row.chatId).mutateAsync({ topic_id: row.topic.id, closeTodoist: false })
      setUndo({
        chatId: row.chatId,
        topicId: row.topic.id,
        message: `"${row.topic.title || '(untitled)'}" archived`,
      })
      if (destination) navigate(destination, { replace: true })
      return true
    } catch {
      setActionError(`Could not archive "${row.topic.title || '(untitled)'}".`)
      return false
    }
  }, [active, closeFor, location.pathname, location.search, navigate])

  const reopenRow = useCallback((row: CodeRow) => {
    setActionError(null)
    reopenFor(row.chatId).mutate({ topic_id: row.topic.id, reopenTodoist: false }, {
      onError: () => setActionError(
        `Could not reopen "${row.topic.title || '(untitled)'}". It stays archived.`,
      ),
    })
  }, [reopenFor])

  const renderRow = (row: CodeRow, section: 'active' | 'done') => {
    const draftKey = draftTargetKey(row.chatId, row.topic.id)
    const draft = draftsByChat[draftKey]
    return (
      <GroupTopicRow
        key={`${row.chatId}:${row.topic.id}`}
        chatId={row.chatId}
        topic={row.topic}
        draft={isVisibleDraft(draft) ? draft : null}
        section={section}
        inlineQuickActions
        leadingBadge={<AgentBadge agent={row.agent} size={20} />}
        onOpen={() => openRow(row)}
        onSnoozePick={() => openRow(row)}
        archiveStyle
        onMarkDone={() => archiveRow(row)}
        onReopen={() => reopenRow(row)}
      />
    )
  }

  const loading = claudeTopics.isLoading || codexTopics.isLoading

  return (
    <div className="flex-1">
      {actionError && (
        <div role="alert" className="px-3 py-2 text-[13px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-900">
          {actionError}
        </div>
      )}

      <section>
        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-stone-500">
          Conversations <span className="opacity-75">· {active.length}</span>
        </div>
        {active.length === 0 && !loading && (
          <div className="grid place-items-center px-6 py-14 text-center">
            <MessageSquarePlus className="w-9 h-9 mb-3 text-stone-400" aria-hidden />
            <div className="text-base font-medium mb-1">No coding conversations yet</div>
            <div className="text-sm text-stone-500">
              Tap the button below to start one with Claude or Codex.
            </div>
          </div>
        )}
        {active.map((row) => renderRow(row, 'active'))}
      </section>

      {archived.length > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setArchiveOpen((open) => !open)}
            aria-expanded={archiveOpen}
            className="flex w-full items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider text-stone-500 hover:bg-stone-100/70 dark:hover:bg-stone-800/40"
          >
            {archiveOpen
              ? <ChevronDown className="w-3.5 h-3.5" aria-hidden />
              : <ChevronRight className="w-3.5 h-3.5" aria-hidden />}
            <Archive className="w-3.5 h-3.5" aria-hidden />
            Archived <span className="opacity-75">· {archived.length}</span>
          </button>
          {archiveOpen && archived.map((row) => renderRow(row, 'done'))}
        </section>
      )}

      {undo && (
        <UndoToast
          message={undo.message}
          onUndo={async () => {
            await reopenFor(undo.chatId).mutateAsync({
              topic_id: undo.topicId,
              reopenTodoist: false,
            })
            setUndo(null)
          }}
          onDismiss={() => setUndo(null)}
        />
      )}
    </div>
  )
}
